import { useState, type FormEvent } from "react";
import { Checkbox, FormControlLabel } from "@mui/material";
import { useTranslation } from "react-i18next";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import { InputWithRef } from "@/components/forms/inputsWithRef/inputWithRef";
import RestrictedPage from "@/consumer/restrictedPage";
import AuditLogChangesPreview from "@/components/auditLog/AuditLogChangesPreview";
import AuditLogDetailDialog from "@/components/auditLog/AuditLogDetailDialog";
import type { AuditLogSummary } from "@/components/auditLog/auditLogTypes";
import type { CrudColumnDef } from "@/components/tables/crudColumnDef";
import { replaceDate } from "@/helper/dateHandling";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";
import { auditLogEntityAdapter } from "@/routes/admin/auditLogEntityAdapter";
import styles from "@/routes/admin/auditLog.module.css";

const isUuid = (value: string) =>
  /^[0-9a-f]{8}(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(value.trim());

const initialSearch = {
  text: "",
  includeRelated: false,
  searchInChanges: false,
};

export default function AuditLog() {
  const { t } = useTranslation();
  usePageHeading(t("audit_log"));
  const [draft, setDraft] = useState(initialSearch);
  const [submitted, setSubmitted] = useState(initialSearch);
  const [refresh, setRefresh] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<AuditLogSummary | null>(
    null
  );
  const hasFilters =
    draft.text !== "" ||
    submitted.text !== "" ||
    draft.includeRelated ||
    draft.searchInChanges;
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setSelectedEntry(null);
    setSubmitted({
      text: draft.text.trim(),
      includeRelated: isUuid(draft.text) && draft.includeRelated,
      searchInChanges: isUuid(draft.text) && draft.searchInChanges,
    });
    setRefresh((value) => !value);
  };
  const reset = () => {
    setDraft(initialSearch);
    setSubmitted(initialSearch);
    setSelectedEntry(null);
  };
  const columns: CrudColumnDef[] = [
    { header: t("id"), accessorKey: "id", id: "id" },
    {
      header: t("audit_diff_object_id"),
      accessorKey: "object_pk",
      id: "object_pk",
    },
    {
      header: t("audit_diff_object_description"),
      accessorFn: (row) => row.object_repr_preview,
      id: "object_repr",
      customColumnFilterId: "object_repr",
      Cell: ({ row }) => (
        <span>
          {row.original.object_repr_preview}
          {row.original.object_repr_truncated && "…"}
        </span>
      ),
    },
    {
      header: t("audit_diff_changes"),
      accessorFn: (row) => row.changes_preview,
      id: "changes",
      customColumnFilterId: "changes",
      customFilterParameter: "__icontains",
      enableSorting: false,
      size: 560,
      Cell: ({ row }) => (
        <AuditLogChangesPreview
          entry={row.original}
          onOpen={() => setSelectedEntry(row.original)}
        />
      ),
    },
    {
      header: t("created_on"),
      accessorKey: "timestamp",
      id: "timestamp",
      filterVariant: "date",
      sortingFn: "datetime",
      Cell: ({ row }) => (
        <span>{replaceDate(row.original.timestamp, true, true)}</span>
      ),
    },
    {
      header: t("modified_by"),
      accessorKey: "actor",
      id: "actor",
      customColumnFilterId: "actor__email",
      customFilterParameter: "__icontains",
      customSortingKey: "actor_email",
      Cell: ({ row }) => <span>{row.original.actor.email ?? ""}</span>,
    },
  ];

  return (
    <RestrictedPage permissionRequired="frontend_view_auditlog">
      <GenericCrudTable
        entity="auditlog"
        entityAdapter={auditLogEntityAdapter}
        columns={columns}
        disallowDelete
        disallowEdit
        disableAddItem
        showRowActions={false}
        disableGlobalFilter
        allowColumnFiltering
        showColumnFilterbyDefault
        customPageSize={50}
        resetPaginationOnFilterChange
        refreshTable={refresh}
        additionalUrlParameter={{
          view: "summary",
          search: encodeURIComponent(submitted.text),
          include_related: String(submitted.includeRelated),
          search_in_changes: String(submitted.searchInChanges),
        }}
        hasAdditionalFiltersToReset={hasFilters}
        onResetAllFilters={reset}
        customTopToolBarAction={() => (
          <form className={styles.searchForm} onSubmit={submit}>
            <div className={styles.searchRow}>
              <InputWithRef
                name="audit-log-search"
                label={t("audit_search_label")}
                value={draft.text}
                onChange={(event) =>
                  setDraft({ ...initialSearch, text: event.target.value })
                }
                className={styles.searchInput}
              />
              <GenericButton type="submit" disabled={!draft.text.trim()}>
                {t("audit_search_submit")}
              </GenericButton>
              <GenericButton
                variant="outlined"
                disabled={!hasFilters}
                onClick={reset}
              >
                {t("audit_search_clear")}
              </GenericButton>
            </div>
            <div className={styles.options}>
              <FormControlLabel
                label={t("audit_search_in_changes")}
                control={
                  <Checkbox
                    checked={draft.searchInChanges}
                    disabled={!isUuid(draft.text)}
                    onChange={(_, checked) =>
                      setDraft({ ...draft, searchInChanges: checked })
                    }
                  />
                }
              />
              <FormControlLabel
                label={t("audit_search_related")}
                control={
                  <Checkbox
                    checked={draft.includeRelated}
                    disabled={!isUuid(draft.text)}
                    onChange={(_, checked) =>
                      setDraft({ ...draft, includeRelated: checked })
                    }
                  />
                }
              />
            </div>
            <span className={styles.searchHint}>
              {t(
                isUuid(draft.text) || !draft.text.trim()
                  ? "audit_search_hint"
                  : "audit_search_freetext_hint"
              )}
            </span>
          </form>
        )}
      />
      <AuditLogDetailDialog
        entry={selectedEntry}
        onClose={() => setSelectedEntry(null)}
      />
    </RestrictedPage>
  );
}
